import {
  BreadcrumbGroupProps,
  Button,
  ButtonDropdown,
  SpaceBetween,
} from '@cloudscape-design/components';
import { default as React, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useStore } from '../store/store';

import {
  ColumnConfiguration,
  FilteringProperties,
} from '../components/devices-table/deviceTableConfig';
import { CarHistoryModal } from '../components/carHistoryModal';
import EditCarsModal from '../components/editCarsModal';
import { SimpleHelpPanelLayout } from '../components/help-panels/simple-help-panel';
import { PageLayout } from '../components/pageLayout';
import { PageTable } from '../components/pageTable';
import { TableHeader } from '../components/tableConfig';
import { useCarCmdApi } from '../hooks/useCarsApi';
import { Car } from '../types/domain';
import { Breadcrumbs } from './fleets/support-functions/supportFunctions';

type OnlineStatus = 'Online' | 'Offline';

const AdminDevices: React.FC = () => {
  const { t } = useTranslation(['translation', 'help-admin-cars']);
  const [state, dispatch] = useStore();

  const eventsById = useMemo(() => {
    const map: Record<string, { eventName?: string }> = {};
    for (const ev of state.events?.events ?? []) {
      if (ev?.eventId) map[ev.eventId] = { eventName: ev.eventName };
    }
    return map;
  }, [state.events?.events]);

  const [carsToDisplay, setCarsToDisplay] = useState<Car[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [selectedItems, setSelectedItems] = useState<Car[]>([]);
  const [online, setOnline] = useState<OnlineStatus>('Online');
  const [onlineBool, setOnlineBool] = useState<boolean>(true);
  const [refresh, setRefresh] = useState<boolean>(false);
  const [columnConfiguration] = useState(() => ColumnConfiguration());
  const [filteringProperties] = useState(() => FilteringProperties());
  const [historyModalVisible, setHistoryModalVisible] = useState<boolean>(false);
  const { getLabelSync } = useCarCmdApi();

  // "View history" is only useful when a single car is selected AND
  // that car has a ChassisSerial tag — older cars activated before the
  // chassis-serial capture work landed won't have one, so the modal
  // would just be empty.
  const singleSelectedCar = selectedItems.length === 1 ? selectedItems[0] : null;
  const historyButtonDisabled = !singleSelectedCar?.ChassisSerial;

  const reloadCars = async (): Promise<void> => {
    setIsLoading(true);
    setRefresh(true);
    dispatch('REFRESH_CARS', !onlineBool);
  };

  useEffect(() => {
    setIsLoading(state.cars?.isLoading ?? false);
  }, [state.cars]);

  useEffect(() => {
    const onlineBool_ = online === 'Online';
    const updatedCars = (state.cars?.cars ?? []).filter((car) =>
      onlineBool_ ? car.PingStatus === 'Online' : car.PingStatus !== 'Online'
    );
    setOnlineBool(onlineBool_);
    setCarsToDisplay(updatedCars);
    setIsLoading(state.cars?.isLoading ?? false);
    return () => {
      // Unmounting
    };
  }, [online, state.cars]);

  useEffect(() => {
    if (refresh) {
      setSelectedItems([]);
      setRefresh(false);
    }
    return () => {
      // Unmounting
    };
  }, [refresh]);

  function getLabels(): void {
    selectedItems.forEach((selectedCar) => {
      getLabelSync(selectedCar.InstanceId, selectedCar.ComputerName);
    });
  }

  const HeaderActionButtons: React.FC = () => {
    return (
      <SpaceBetween direction="horizontal" size="xs">
        <Button iconName="refresh" variant="normal" disabled={isLoading} onClick={reloadCars} />
        <ButtonDropdown
          items={[
            { text: t('devices.online'), id: 'Online', disabled: false },
            {
              text: t('devices.offline'),
              id: 'Offline',
              disabled: false,
            },
          ]}
          onItemClick={({ detail }) => {
            setOnline(detail.id as OnlineStatus);
            setSelectedItems([]);
          }}
        >
          {online}
        </ButtonDropdown>
        <EditCarsModal
          disabled={selectedItems.length === 0}
          setRefresh={setRefresh}
          selectedItems={selectedItems}
          online={onlineBool}
          variant="primary"
        />
        <Button variant="primary" onClick={getLabels} disabled={selectedItems.length === 0}>
          {selectedItems.length > 1
            ? t('label-printer.download-printable-labels')
            : t('label-printer.download-printable-label')}
        </Button>
        <Button disabled={historyButtonDisabled} onClick={() => setHistoryModalVisible(true)}>
          {t('devices.view-car-history')}
        </Button>
      </SpaceBetween>
    );
  };

  const breadcrumbs: BreadcrumbGroupProps.Item[] = Breadcrumbs();
  breadcrumbs.push({ text: t('devices.breadcrumb'), href: '#' });

  return (
    <PageLayout
      helpPanelHidden={false}
      helpPanelContent={
        <SimpleHelpPanelLayout
          headerContent={t('header', { ns: 'help-admin-cars' })}
          bodyContent={t('content', { ns: 'help-admin-cars' })}
          footerContent={t('footer', { ns: 'help-admin-cars' })}
        />
      }
      header={t('devices.header')}
      description={t('devices.description')}
      breadcrumbs={breadcrumbs}
    >
      <PageTable
        selectedItems={selectedItems}
        setSelectedItems={setSelectedItems}
        tableItems={carsToDisplay}
        selectionType="multi"
        columnConfiguration={columnConfiguration}
        header={
          <TableHeader
            nrSelectedItems={selectedItems.length}
            nrTotalItems={carsToDisplay.length}
            header={t('devices.header')}
            actions={(<HeaderActionButtons />) as any}
          />
        }
        itemsIsLoading={isLoading}
        loadingText={t('devices.loading')}
        localStorageKey={'cars-table-preferences'}
        trackBy={'InstanceId'}
        filteringProperties={filteringProperties as any}
        filteringI18nStringsName={'devices'}
      />
      <CarHistoryModal
        visible={historyModalVisible}
        onDismiss={() => setHistoryModalVisible(false)}
        chassisSerial={singleSelectedCar?.ChassisSerial || ''}
        eventsById={eventsById}
      />
    </PageLayout>
  );
};

export { AdminDevices };
